import React, { useState } from "react";
import { format } from "date-fns";
import { Typography, Box, ButtonBase, Chip, Stack } from "@mui/material";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import Modal from "@mui/material/Modal";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";

import { formatRelativeDate, formatDistanceCustom } from "./deedListUtils";
import DeedEdit from "../DeedEdit/DeedEdit";
import { useDeedsUI } from "../DeedsUIContext";
import { picUrl } from "../../../shared/picUrl";

export const DeedGroup = ({ group, sortedDeeds, isEditMode, setIsEditMode }) => {
  const theme = useTheme();
  const { addDescriptionChip, addBucketChip } = useDeedsUI();

  const [selectedDeedId, setSelectedDeedId] = useState(null);
  const [open, setOpen] = useState(false);

  const handleOpen = (deedId) => {
    setOpen(true);
    setIsEditMode(true);
    setSelectedDeedId(deedId);
  };
  const handleClose = () => setOpen(false);

  const handleDescriptionClick = (event, description) => {
    event.preventDefault();
    addDescriptionChip(description);
  };

  const handleBucketClick = (event, bucket) => {
    event.preventDefault();
    addBucketChip(bucket);
  };

  const handleImageClick = (pic, event) => {
    event.preventDefault();
    window.open(picUrl(pic), "_blank");
  };

  return (
    <>
      <Container
        sx={{
          px: { xs: 0.5, sm: 2 },
          pb: 4,
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: { xs: "343px", sm: "600px" } }}>
          <Typography
            variant="h6"
            pl={0.5}
            pb={2}
            fontWeight="bold"
            textAlign="left"
          >
            {formatRelativeDate(new Date(group.label))}
          </Typography>
        </Box>

        {group.deeds.map((deed) => (
          <Box
            key={deed.id}
            border="1px solid #E0E0E0"
            borderRadius="20px"
            boxShadow={1}
            padding={2}
            margin={1}
            sx={{ width: { xs: "343px", sm: "600px" } }}
          >
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between" alignItems="baseline">
                <Box display="flex" justifyContent="flex-start" alignItems="center" gap="8px">
                  {deed.bucket && (
                    <Chip
                      variant="filled"
                      size="small"
                      sx={{
                        cursor: "pointer",
                        height: "auto",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          padding: "4px 12px",
                        },
                      }}
                      label={
                        <Typography variant="caption">{deed.bucket}</Typography>
                      }
                      onClick={(event) => handleBucketClick(event, deed.bucket)}
                    />
                  )}
                  {deed.timeSpent !== 0 && (
                    <Typography variant="caption">{deed.timeSpent}m</Typography>
                  )}
                  <Typography variant="caption">
                    {formatDistanceCustom(new Date(deed.dateTime))} ago
                  </Typography>
                </Box>

                <Typography variant="body2">
                  {format(new Date(deed.dateTime), "h:mm aa")}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="bottom">
                <Box pl="4px">
                  <ButtonBase
                    onClick={(event) =>
                      handleDescriptionClick(event, deed.description)
                    }
                  >
                    <Typography variant="body1" fontWeight={500} textAlign="left">
                      {deed.description}
                    </Typography>
                  </ButtonBase>
                </Box>
                {!deed.notes && deed.pics.length === 0 && (
                  <Box textAlign="right">
                    <IconButton onClick={() => handleOpen(deed.id)}>
                      <MoreHorizOutlinedIcon
                        sx={{
                          fontSize: "20px",
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {(deed.notes || deed.pics.length > 0) && (
                <Box display="flex" justifyContent="space-between">
                  <Stack spacing={2}>
                    {deed.notes && (
                      <Box textAlign="left" pl="4px">
                        <Typography
                          variant="caption"
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {deed.notes}
                        </Typography>
                      </Box>
                    )}
                    {deed.pics.length > 0 && (
                      <Box display="flex" flexWrap="wrap" justifyContent="flex-start" alignItems="center">
                        {deed.pics.map((pic, picIndex) => (
                          <Box key={picIndex} m={0.5}>
                            <img
                              src={picUrl(pic)}
                              alt="deed attachment"
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={(event) => handleImageClick(pic, event)}
                              role="button"
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Stack>
                  <Box display="flex" alignItems="end">
                    <IconButton onClick={() => handleOpen(deed.id)}>
                      <MoreHorizOutlinedIcon
                        sx={{
                          fontSize: "20px",
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        ))}
      </Container>
      <Modal open={open} onClose={handleClose}>
        <DeedEdit
          sortedDeeds={sortedDeeds}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          selectedDeedId={selectedDeedId}
          handleClose={handleClose}
        />
      </Modal>
    </>
  );
};
